import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import { projectService, apiErrorMessage } from "../../api/taskBoardService";
import CatalogDetailLayout from "../../components/task-board/CatalogDetailLayout";
import { ProjectFormModal } from "../../components/task-board/CatalogFormModals";
import { EnvironmentLink, ProjectLabel, SectionCard, formatDate } from "../../components/task-board/shared";

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const admin = isAdmin(auth?.employee);
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      setProject((await projectService.get(id)).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load the project"));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <CatalogDetailLayout
        backTo="/task-board/projects"
        backLabel="All projects"
        title={project?.name || "Project"}
        subtitle={project && `Updated ${formatDate(project.updatedAt)}`}
        loading={!project && !error}
        error={error}
        onRetry={load}
        admin={admin}
        entityLabel="project"
        onEdit={() => setEditing(true)}
        onDelete={async () => {
          await projectService.remove(id);
          navigate("/task-board/projects", { replace: true });
        }}
        boardFilter={`project=${id}`}
        taskCounts={project?.taskCounts}
      >
        {project && (
          <>
            <SectionCard title="Overview">
              <div className="mb-3">
                <ProjectLabel project={project} />
              </div>
              {project.description ? (
                <p className="text-sm text-gray-800 dark:text-light-200 whitespace-pre-wrap">{project.description}</p>
              ) : (
                <p className="text-sm italic text-gray-400 dark:text-light-500">No description</p>
              )}
            </SectionCard>
            <SectionCard title="Environments">
              <div className="space-y-3">
                <EnvironmentLink label="Development" url={project.developmentUrl} />
                <EnvironmentLink label="Production" url={project.productionUrl} />
              </div>
            </SectionCard>
          </>
        )}
      </CatalogDetailLayout>
      {project && (
        <ProjectFormModal
          isOpen={editing}
          project={project}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      )}
    </>
  );
};

export default ProjectDetails;
